import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import {
  getSnapshot as getExpiredSession,
  subscribe as subscribeToExpiredSession,
} from '@configs/axios/session-expired-store/sessionExpiredStore';
import * as React from 'react';

import { useMsalAuth } from '../msal-provider';
import {
  AuthContext,
  type AuthContextValue,
  type AuthProviderProps,
  type AuthState,
  type AuthStatus,
  type AuthType,
} from './AuthProvider.types';
import {
  INITIALIZING_STATE,
  resolveAuthState,
  UNAUTHENTICATED_STATE,
} from './AuthProvider.utils';

const AuthProvider = React.memo(
  ({ children }: Readonly<AuthProviderProps>): React.ReactElement => {
    const { instance, accounts, inProgress } = useMsal();
    const { login: msalLogin, logout: msalLogout } = useMsalAuth();
    const [authState, setAuthState] =
      React.useState<AuthState>(INITIALIZING_STATE);
    const [validationAttempt, setValidationAttempt] = React.useState(0);
    const expiredSession = React.useSyncExternalStore(
      subscribeToExpiredSession,
      getExpiredSession
    );

    React.useEffect(() => {
      if (
        inProgress !== InteractionStatus.None &&
        inProgress !== InteractionStatus.Startup
      ) {
        setAuthState(INITIALIZING_STATE);
        return;
      }

      let isCurrent = true;
      resolveAuthState(instance, accounts).then(state => {
        if (isCurrent) {
          setAuthState(state);
        }
      });
      return () => {
        isCurrent = false;
      };
    }, [accounts, inProgress, instance, validationAttempt]);

    const retry = React.useCallback((): void => {
      setAuthState(INITIALIZING_STATE);
      setValidationAttempt(attempt => attempt + 1);
    }, []);

    const login = React.useCallback(
      async (type: AuthType): Promise<void> => {
        await msalLogin(type);
      },
      [msalLogin]
    );

    const logout = React.useCallback(async (): Promise<void> => {
      setAuthState(UNAUTHENTICATED_STATE);
      await msalLogout();
    }, [msalLogout]);

    const value = React.useMemo((): AuthContextValue => {
      const status: AuthStatus = expiredSession
        ? 'sessionExpired'
        : authState.status;

      return {
        status,
        authData:
          authState.status === 'authenticated' && !expiredSession
            ? authState.authData
            : null,
        isAuthenticated: status === 'authenticated',
        isLoading: status === 'initializing',
        retry,
        login,
        logout,
      };
    }, [authState, expiredSession, login, logout, retry]);

    return <AuthContext value={value}>{children}</AuthContext>;
  }
);

AuthProvider.displayName = 'AuthProvider';

export { AuthProvider };
